import { DateTime } from 'luxon';
import {
    applyPeriod,
    isPeriod,
    PeriodValue,
} from '@instinet-analytics/semantic-ui-components';

export default class DateTimeExpression {
    private datetime: DateTime;
    private startOfDay: DateTime;
    private startOfNextDay: DateTime;
    private startOfMinute: DateTime;
    private startOfNextMinute: DateTime;

    public constructor(
        targetMillisOrTodayPeriod: number | string | PeriodValue
    ) {
        if (targetMillisOrTodayPeriod === 'Today') {
            this.datetime = DateTime.fromJSDate(new Date());
        } else if (isPeriod(targetMillisOrTodayPeriod)) {
            this.datetime = applyPeriod(targetMillisOrTodayPeriod, new Date());
        } else if (typeof targetMillisOrTodayPeriod === 'string') {
            throw new Error(`Invalid value ${targetMillisOrTodayPeriod}`);
        } else {
            const targetMillis = targetMillisOrTodayPeriod as number;
            this.datetime = DateTime.fromMillis(targetMillis);
        }
        // Create a DateTime from a number of milliseconds since the epoch
        // (i.e. since 1 January 1970 00:00:00 UTC). Uses the default zone.
        this.startOfDay = this.datetime.startOf('day');
        this.startOfNextDay = this.datetime.plus({ days: 1 }).startOf('day');
        this.startOfMinute = this.datetime.startOf('minute');
        this.startOfNextMinute = this.datetime
            .plus({ minutes: 1 })
            .startOf('minute');
    }

    public testSameDay(ms: number) {
        const testDate = DateTime.fromMillis(ms);
        return this.datetime.hasSame(testDate, 'day');
    }

    public testNotSameDay(ms: number) {
        return !this.testSameDay(ms);
    }

    public testBeforeThanDay(ms: number) {
        return DateTime.fromMillis(ms).startOf('day') < this.startOfDay;
    }

    public testBeforeThanOrSameDay(ms: number) {
        return DateTime.fromMillis(ms).startOf('day') < this.startOfNextDay;
    }

    public testAfterThanDay(ms: number) {
        return DateTime.fromMillis(ms).startOf('day') >= this.startOfNextDay;
    }

    public testAfterThanOrSameDay(ms: number) {
        return DateTime.fromMillis(ms).startOf('day') >= this.startOfDay;
    }

    public testTimeGreaterThan(ms: number) {
        const dateToFilter = new Date(ms);
        this.setDateToEpoch(dateToFilter);
        return dateToFilter.getTime() > this.datetime.toMillis();
    }

    public testTimeGreaterThanOrEqual(ms: number) {
        const dateToFilter = new Date(ms);
        this.setDateToEpoch(dateToFilter);
        return dateToFilter.getTime() >= this.datetime.toMillis();
    }

    public testTimeLessThan(ms: number) {
        const dateToFilter = new Date(ms);
        this.setDateToEpoch(dateToFilter);
        return dateToFilter.getTime() < this.datetime.toMillis();
    }

    public testTimeLessThanOrEqual(ms: number) {
        const dateToFilter = new Date(ms);
        this.setDateToEpoch(dateToFilter);
        return dateToFilter.getTime() <= this.datetime.toMillis();
    }

    public testSameTime(ms: number) {
        return this.testSameHour(ms) && this.testSameMinute(ms);
    }

    public testNotSameTime(ms: number) {
        return !this.testSameTime(ms);
    }

    public testSameHour(ms: number) {
        const dateToFilter = new Date(ms);
        this.setDateToEpoch(dateToFilter);
        const dateTimeToFilter = DateTime.fromJSDate(dateToFilter);

        const date = this.datetime.toJSDate();
        this.setDateToEpoch(date);
        return DateTime.fromJSDate(date).hasSame(dateTimeToFilter, 'hour');
    }

    public testSameMinute(ms: number) {
        const dateToFilter = new Date(ms);
        this.setDateToEpoch(dateToFilter);
        const dateTimeToFilter = DateTime.fromJSDate(dateToFilter);

        const date = this.datetime.toJSDate();
        this.setDateToEpoch(date);
        return DateTime.fromJSDate(date).hasSame(dateTimeToFilter, 'minute');
    }

    private setDateToEpoch(date: Date): void {
        date.setFullYear(1970, 0, 1);
    }

    public testNotSameMinute(ms: number) {
        return !this.testSameMinute(ms);
    }

    public testBeforeThanMinute(ms: number) {
        return DateTime.fromMillis(ms).startOf('minute') < this.startOfMinute;
    }

    public testBeforeThanOrSameMinute(ms: number) {
        return (
            DateTime.fromMillis(ms).startOf('minute') < this.startOfNextMinute
        );
    }

    public testAfterThanMinute(ms: number) {
        return (
            DateTime.fromMillis(ms).startOf('minute') >= this.startOfNextMinute
        );
    }

    public testAfterThanOrSameMinute(ms: number) {
        return DateTime.fromMillis(ms).startOf('minute') >= this.startOfMinute;
    }
}
